/**
 * Centralized category configuration for pregnancy-related recalls
 * 
 * BASE_CATEGORIES: Baby food only (25)
 * EXTRA_CATEGORIES: All other food categories including dairy (64)
 * EVALUATION_CATEGORIES: Behind ALLOW_EVALUATION flag (experimental)
 * EXCLUDED_CATEGORIES: Never processed without pathogen filter
 */

// Base = uniquement aliments bébé
export const BASE_CATEGORIES = ['25'];

// Extra = tous les autres produits alimentaires (incluant laitiers)
export const EXTRA_CATEGORIES = [
  '64',  // Lait et produits laitiers
  '40',  // Plats préparés
  '45',  // Sandwiches
  '50',  // Pâtisseries
  '51',  // Desserts
  '73',  // Salades prêtes à consommer
  '76',  // Soupes
  '79',  // Sauces
  '80',  // Condiments
  '85',  // Glaces
  '94',  // Produits surgelés
  '100'  // Autres produits alimentaires
];

export const EVALUATION_CATEGORIES = ['61', '89'];

export const EXCLUDED_CATEGORIES = ['18', '19', '20', '21'];

export const ALLOW_EVALUATION = false;

// Pathogènes critiques pour le badge Grossesse (risques fœtus)
export const PREGNANCY_CRITICAL_PATHOGENS = ['listeria', 'toxoplasma', 'salmonella'];

export const CATEGORY_LABELS: Record<string, string> = {
  '25': 'Aliments pour bébés',
  '64': 'Lait et produits laitiers',
  '40': 'Plats préparés',
  '45': 'Sandwiches',
  '50': 'Pâtisseries',
  '51': 'Desserts',
  '73': 'Salades prêtes à consommer',
  '76': 'Soupes',
  '79': 'Sauces',
  '80': 'Condiments',
  '85': 'Glaces',
  '94': 'Produits surgelés',
  '100': 'Autres produits alimentaires',
  '61': 'Évaluation: Fruits et légumes',
  '89': 'Évaluation: Céréales',
  '18': 'Viandes',
  '19': 'Produits de la mer',
  '20': 'Charcuterie prête à consommer',
  '21': 'Œufs et ovoproduits'
};

/**
 * Returns allowed category IDs - EXTRA_CATEGORIES now included by default for all users
 * @param extended - Whether to include EVALUATION_CATEGORIES (experimental)
 * @returns Array of allowed category IDs
 */
export function allowedCategories({ extended }: { extended: boolean }): string[] {
  // BASE + EXTRA = toutes les catégories alimentaires courantes
  const core = [...BASE_CATEGORIES, ...EXTRA_CATEGORIES];
  const evalCats = ALLOW_EVALUATION ? EVALUATION_CATEGORIES : [];
  return extended ? [...core, ...evalCats] : core;
}

/**
 * Check if a category should be excluded from ingestion
 * @param categoryId - Category ID to check
 * @returns true if category should be rejected
 */
export function isExcludedCategory(categoryId: string): boolean {
  return (EXCLUDED_CATEGORIES as readonly string[]).includes(categoryId);
}

/**
 * Pilot categories (Viandes, Produits de la mer, Charcuterie, Œufs)
 * Incluses par défaut dans useRecalls.ts — cette config sert de référence.
 */
export const PILOT_CATEGORIES = {
  categories: ['18', '19', '20', '21'],
  labels: {
    '18': 'Viandes',
    '19': 'Produits de la mer',
    '20': 'Charcuterie prête à consommer',
    '21': 'Œufs et ovoproduits'
  }
};

/**
 * Push notification scope categories
 * Defines which categories are included in each notification scope
 */
export const PUSH_SCOPE_CATEGORIES = {
  base: ['25'],
  etendu: [
    '25',
    ...EXTRA_CATEGORIES,
    ...PILOT_CATEGORIES.categories
  ]
};
